import { useEffect, useState } from 'react';
import {
    ActionIcon,
    Anchor,
    Button,
    Card,
    Checkbox,
    Group,
    Modal,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { api } from '@/api/client';
import { useLocations } from '@/api/products';
import type { DaySchedule, Location } from '@/api/types';
import { LockerBadges } from '@/components/LockerBadges';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { notifyError, notifySuccess } from '@/lib/notify';
import { DAYS, DEFAULT_DAY, normalizeHours, serializeHours, summarizeHours, type NormalizedHours } from '@/lib/schedule';

function DayRow({
    label,
    day,
    onChange,
    extra,
}: {
    label: React.ReactNode;
    day: DaySchedule;
    onChange: (d: DaySchedule) => void;
    extra?: React.ReactNode;
}) {
    const disabledTime = !!day.closed || !!day.all_day;
    return (
        <Group gap="sm" wrap="wrap" align="center">
            <div style={{ minWidth: 40 }}>{label}</div>
            <Checkbox
                size="xs"
                label="выходной"
                checked={!!day.closed}
                onChange={(e) => onChange({ ...day, closed: e.currentTarget.checked })}
            />
            <Checkbox
                size="xs"
                label="24 ч"
                disabled={!!day.closed}
                checked={!!day.all_day}
                onChange={(e) => onChange({ ...day, all_day: e.currentTarget.checked })}
            />
            <Group gap={6} wrap="nowrap">
                <TextInput
                    size="xs"
                    type="time"
                    w={96}
                    disabled={disabledTime}
                    value={day.from ?? DEFAULT_DAY.from}
                    onChange={(e) => onChange({ ...day, from: e.currentTarget.value })}
                />
                <Text size="xs" c="dimmed">
                    –
                </Text>
                <TextInput
                    size="xs"
                    type="time"
                    w={96}
                    disabled={disabledTime}
                    value={day.to ?? DEFAULT_DAY.to}
                    onChange={(e) => onChange({ ...day, to: e.currentTarget.value })}
                />
            </Group>
            {extra}
        </Group>
    );
}

interface Form {
    name: string;
    country: string;
    city: string;
    address: string;
    zip_code: string;
    latitude: string;
    longitude: string;
    yandex: string;
    gis: string;
    hours: NormalizedHours;
}

function toForm(l: Location | null): Form {
    return {
        name: l?.name ?? '',
        country: l?.country ?? 'Россия',
        city: l?.city ?? '',
        address: l?.address ?? '',
        zip_code: l?.zip_code ?? '',
        latitude: l?.latitude != null ? String(l.latitude) : '',
        longitude: l?.longitude != null ? String(l.longitude) : '',
        yandex: l?.yandex_map_link ?? '',
        gis: l?.['2gis_map_link'] ?? '',
        hours: normalizeHours(l?.working_hours),
    };
}

function LocationModal({ location, opened, onClose }: { location: Location | null; opened: boolean; onClose: () => void }) {
    const queryClient = useQueryClient();
    const fullScreen = useMediaQuery('(max-width: 48em)');
    const [form, setForm] = useState<Form>(toForm(location));

    useEffect(() => {
        if (opened) setForm(toForm(location));
    }, [opened, location]);

    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));
    const setDay = (d: number, day: DaySchedule) =>
        setForm((f) => ({ ...f, hours: { ...f.hours, regular: { ...f.hours.regular, [d]: day } } }));
    const setException = (i: number, patch: Partial<NormalizedHours['exceptions'][number]>) =>
        setForm((f) => ({
            ...f,
            hours: { ...f.hours, exceptions: f.hours.exceptions.map((e, j) => (j === i ? { ...e, ...patch } : e)) },
        }));

    const save = useMutation({
        mutationFn: () => {
            const body = {
                name: form.name.trim(),
                country: form.country.trim(),
                city: form.city.trim(),
                address: form.address.trim(),
                zip_code: form.zip_code.trim(),
                latitude: parseFloat(form.latitude) || null,
                longitude: parseFloat(form.longitude) || null,
                working_hours: serializeHours(form.hours),
                yandex_map_link: form.yandex.trim(),
                '2gis_map_link': form.gis.trim(),
            };
            return location ? api.put(`/api/admin/locations/${location.id}`, body) : api.post('/api/admin/locations', body);
        },
        onSuccess: () => {
            notifySuccess('Локация сохранена');
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            onClose();
        },
        onError: notifyError,
    });

    return (
        <Modal opened={opened} onClose={onClose} title={location ? 'Локация' : 'Новая локация'} size="xl" fullScreen={fullScreen}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save.mutate();
                }}
            >
                <Stack>
                    <TextInput label="Название" required value={form.name} onChange={(e) => set('name', e.currentTarget.value)} />
                    <SimpleGrid cols={{ base: 1, xs: 2 }}>
                        <TextInput label="Страна" value={form.country} onChange={(e) => set('country', e.currentTarget.value)} />
                        <TextInput label="Город" required value={form.city} onChange={(e) => set('city', e.currentTarget.value)} />
                    </SimpleGrid>
                    <TextInput
                        label="Адрес (улица, дом)"
                        required
                        value={form.address}
                        onChange={(e) => set('address', e.currentTarget.value)}
                    />
                    <SimpleGrid cols={{ base: 1, xs: 3 }}>
                        <TextInput label="Индекс" value={form.zip_code} onChange={(e) => set('zip_code', e.currentTarget.value)} />
                        <TextInput label="Широта" inputMode="decimal" value={form.latitude} onChange={(e) => set('latitude', e.currentTarget.value)} />
                        <TextInput label="Долгота" inputMode="decimal" value={form.longitude} onChange={(e) => set('longitude', e.currentTarget.value)} />
                    </SimpleGrid>

                    <Card padding="md">
                        <Text fw={700} mb="xs">
                            Режим работы
                        </Text>
                        <Text size="xs" c="dimmed" mb="sm">
                            Время московское.
                        </Text>
                        <Stack gap="xs">
                            {DAYS.map((label, i) => (
                                <DayRow
                                    key={label}
                                    label={<Text fw={600} size="sm">{label}</Text>}
                                    day={form.hours.regular[i + 1] ?? { closed: true }}
                                    onChange={(d) => setDay(i + 1, d)}
                                />
                            ))}
                        </Stack>
                    </Card>

                    <Card padding="md">
                        <Group justify="space-between" mb="sm">
                            <Text fw={700}>Исключения (праздники)</Text>
                            <Button
                                size="compact-sm"
                                variant="light"
                                leftSection={<Plus size={14} />}
                                onClick={() =>
                                    set('hours', {
                                        ...form.hours,
                                        exceptions: [...form.hours.exceptions, { date: '', from: '10:00', to: '18:00' }],
                                    })
                                }
                            >
                                Добавить
                            </Button>
                        </Group>
                        <Stack gap="sm">
                            {form.hours.exceptions.length === 0 && (
                                <Text size="sm" c="dimmed">
                                    Нет исключений
                                </Text>
                            )}
                            {form.hours.exceptions.map((ex, i) => (
                                <DayRow
                                    key={i}
                                    label={
                                        <TextInput
                                            size="xs"
                                            type="date"
                                            value={ex.date}
                                            onChange={(e) => setException(i, { date: e.currentTarget.value })}
                                        />
                                    }
                                    day={ex}
                                    onChange={(d) => setException(i, d)}
                                    extra={
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            aria-label="Удалить исключение"
                                            onClick={() =>
                                                set('hours', {
                                                    ...form.hours,
                                                    exceptions: form.hours.exceptions.filter((_, j) => j !== i),
                                                })
                                            }
                                        >
                                            <X size={16} />
                                        </ActionIcon>
                                    }
                                />
                            ))}
                        </Stack>
                    </Card>

                    <SimpleGrid cols={{ base: 1, xs: 2 }}>
                        <TextInput label="Яндекс Карты" value={form.yandex} onChange={(e) => set('yandex', e.currentTarget.value)} />
                        <TextInput label="2ГИС" value={form.gis} onChange={(e) => set('gis', e.currentTarget.value)} />
                    </SimpleGrid>

                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button type="submit" loading={save.isPending}>
                            Сохранить
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}

export function LocationsScreen() {
    const queryClient = useQueryClient();
    const locations = useLocations();
    const [editing, setEditing] = useState<Location | null>(null);
    const [opened, setOpened] = useState(false);

    const remove = useMutation({
        mutationFn: (id: number) => api.delete(`/api/admin/locations/${id}`),
        onSuccess: () => {
            notifySuccess('Локация удалена');
            queryClient.invalidateQueries({ queryKey: ['locations'] });
        },
        onError: notifyError,
    });

    const open = (l: Location | null) => {
        setEditing(l);
        setOpened(true);
    };

    const confirmRemove = (l: Location) =>
        modals.openConfirmModal({
            title: 'Удалить локацию?',
            children: <Text size="sm">«{l.name}» будет удалена без возможности восстановления.</Text>,
            labels: { confirm: 'Удалить', cancel: 'Отмена' },
            confirmProps: { color: 'red' },
            onConfirm: () => remove.mutate(l.id),
        });

    const columns: Column<Location>[] = [
        { key: 'name', title: 'Название', mobile: 'title', render: (l) => l.name },
        {
            key: 'address',
            title: 'Адрес',
            mobile: 'subtitle',
            render: (l) => [l.city, l.address].filter(Boolean).join(', ') || '—',
        },
        { key: 'hours', title: 'Режим', render: (l) => summarizeHours(l.working_hours) },
        {
            key: 'lockers',
            title: 'Постаматы',
            render: (l) => (
                <LockerBadges
                    items={(l.lockers ?? []).map((x) => ({
                        locker_id: x.locker_id,
                        name: x.display_name || `#${x.id}`,
                        online: x.is_online,
                    }))}
                />
            ),
        },
        {
            key: 'maps',
            title: 'Карты',
            mobile: 'hidden',
            render: (l) => (
                <Group gap="xs">
                    {l.yandex_map_link && (
                        <Anchor href={l.yandex_map_link} target="_blank" size="sm" onClick={(e) => e.stopPropagation()}>
                            Яндекс
                        </Anchor>
                    )}
                    {l['2gis_map_link'] && (
                        <Anchor href={l['2gis_map_link']} target="_blank" size="sm" onClick={(e) => e.stopPropagation()}>
                            2ГИС
                        </Anchor>
                    )}
                </Group>
            ),
        },
    ];

    return (
        <Page
            title="Локации"
            actions={
                <Button leftSection={<Plus size={16} />} onClick={() => open(null)}>
                    Добавить
                </Button>
            }
        >
            <QueryState isLoading={locations.isLoading} error={locations.error} onRetry={locations.refetch}>
                <ResponsiveTable
                    data={locations.data ?? []}
                    columns={columns}
                    rowKey={(l) => l.id}
                    onRowClick={open}
                    empty="Локаций нет"
                    actions={(l) => (
                        <>
                            <Button size="compact-sm" variant="light" leftSection={<Pencil size={14} />} onClick={() => open(l)}>
                                Изменить
                            </Button>
                            <ActionIcon variant="light" color="red" aria-label="Удалить" onClick={() => confirmRemove(l)}>
                                <Trash2 size={14} />
                            </ActionIcon>
                        </>
                    )}
                />
            </QueryState>
            <LocationModal location={editing} opened={opened} onClose={() => setOpened(false)} />
        </Page>
    );
}
